/**
 * Module dependencies.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'app/components/atoms/button/button';
import { Modal } from 'app/components/organisms/modal/modal';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Checkbox } from 'app/components/ui/checkbox';
import { SectionHeader } from 'app/components/molecules/section-header/section-header';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useBankAccounts } from 'app/hooks/requests/bank-accounts/use-bank-accounts';
import {
  useCreateMovementShare,
  useMovementShares,
  useRevokeMovementShare
} from 'app/hooks/requests/transactions/use-movement-shares';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { appRoutes } from 'app/routes';
import { cn } from 'app/lib/utils';
import { Copy, KeyRound, Link2, Lock, Share2, Trash2 } from 'lucide-react';

/**
 * Format an ISO/SQL date as a short local date (or a dash).
 */

function fmtDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  const date = new Date(String(value).replace(' ', 'T'));

  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-PT');
}

/**
 * A labelled config item (small uppercase label over the value).
 */

function ConfigItem({
  label,
  children,
  className
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('min-w-0', className)}>
      <div className={'text-[10px] uppercase tracking-wide text-muted-foreground'}>{label}</div>

      <div className={'break-words text-[12px] text-foreground'}>{children}</div>
    </div>
  );
}

/**
 * Selectable columns for a share.
 */

const FIELD_OPTIONS = ['date', 'description', 'category', 'previousBalance', 'amount', 'balance'] as const;

const DEFAULT_FIELDS: string[] = ['date', 'description', 'category', 'previousBalance', 'amount', 'balance'];

/**
 * Build the public URL for a share token.
 */

function shareUrl(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  return `${origin}${resolveAppPath(appRoutes.share.movements, { token })}`;
}

/**
 * Export `ShareMovementsAction` component — creates and manages read-only,
 * anonymously-accessible links to the selected account's movements.
 */

export function ShareMovementsAction(): JSX.Element {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);

  // Current account + active filters come from the movements list URL filter.
  const filter = useMemo(() => {
    try {
      return JSON.parse(searchParams.get('filter') ?? '{}');
    } catch {
      return {} as any;
    }
  }, [searchParams]);

  const accountId = filter?.account ? Number(filter.account) : undefined;

  const { data: accounts } = useBankAccounts();
  const accountName = accounts?.results?.find((item: any) => item.id === accountId)?.name;

  const { data: shares } = useMovementShares(accountId);
  const create = useCreateMovementShare();
  const revoke = useRevokeMovementShare();

  // Form state.
  const [label, setLabel] = useState('');
  const [pin, setPin] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [fields, setFields] = useState<string[]>(DEFAULT_FIELDS);
  const [applyFilters, setApplyFilters] = useState(false);

  const activeFilters = useMemo(
    () => ({
      search: filter?.search,
      category: filter?.category,
      movementType: filter?.movementType,
      transactionType: filter?.transactionType,
      year: filter?.year,
      month: filter?.month
    }),
    [filter]
  );

  const hasActiveFilters = Object.values(activeFilters).some(value => value != null && value !== '' && value !== -1);

  const toggleField = (field: string, checked: boolean) => {
    setFields(prev => (checked ? Array.from(new Set([...prev, field])) : prev.filter(item => item !== field)));
  };

  const pinInvalid = pin.length > 0 && !/^\d{6,}$/.test(pin);

  const onCreate = async () => {
    if (!accountId || pinInvalid || fields.length === 0) {
      return;
    }

    try {
      await create.mutateAsync({
        accountId,
        label: label || undefined,
        pin: pin || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        expiresAt: expiresAt || undefined,
        fields,
        applyFilters,
        filters: applyFilters ? activeFilters : undefined
      });

      setLabel('');
      setPin('');
      success({ message: translate('movementShare.created') });
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? translate('movementShare.createError') });
    }
  };

  const copyValue = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      success({ message });
    } catch {
      error({ message: translate('movementShare.copyError') });
    }
  };

  const copy = (token: string) => copyValue(shareUrl(token), translate('movementShare.copied'));

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Share2 className={'mr-1.5 h-4 w-4'} />
        {translate('movementShare.share')}
      </Button>

      <Modal
        buttons={[]}
        className={'sm:max-w-3xl'}
        isLoading={false}
        isOpen={open}
        maskClosable
        onClose={() => setOpen(false)}
      >
        <div className={'max-h-[75vh] overflow-y-auto'}>
          <SectionHeader
            className={'mb-4'}
            description={
              accountName
                ? translate('movementShare.subtitle', { account: accountName })
                : translate('movementShare.selectAccount')
            }
            icon={<Share2 className={'h-5 w-5'} />}
            title={translate('movementShare.title')}
          />

          {!accountId ? (
            <div
              className={
                'rounded-lg border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground'
              }
            >
              {translate('movementShare.selectAccountHint')}
            </div>
          ) : (
            <div className={'space-y-4'}>
              {/* Create form */}
              <div className={'space-y-3 rounded-lg border border-border p-3'}>
                <div className={'grid gap-3 sm:grid-cols-2'}>
                  <div className={'space-y-1'}>
                    <Label>{translate('movementShare.label')}</Label>

                    <Input
                      onChange={e => setLabel(e.target.value)}
                      placeholder={translate('movementShare.labelPlaceholder')}
                      value={label}
                    />
                  </div>

                  <div className={'space-y-1'}>
                    <Label>{translate('movementShare.pin')}</Label>

                    <Input
                      inputMode={'numeric'}
                      onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                      placeholder={translate('movementShare.pinPlaceholder')}
                      value={pin}
                    />

                    {pinInvalid ? (
                      <p className={'text-[11px] text-destructive'}>{translate('movementShare.pinHint')}</p>
                    ) : (
                      <p className={'text-[11px] text-muted-foreground'}>{translate('movementShare.pinOptional')}</p>
                    )}
                  </div>

                  <div className={'space-y-1'}>
                    <Label>{translate('movementShare.fromDate')}</Label>

                    <Input onChange={e => setFromDate(e.target.value)} type={'date'} value={fromDate} />
                  </div>

                  <div className={'space-y-1'}>
                    <Label>{translate('movementShare.toDate')}</Label>

                    <Input onChange={e => setToDate(e.target.value)} type={'date'} value={toDate} />
                  </div>

                  <div className={'space-y-1'}>
                    <Label>{translate('movementShare.expiresAt')}</Label>

                    <Input onChange={e => setExpiresAt(e.target.value)} type={'date'} value={expiresAt} />
                  </div>
                </div>

                <div className={'space-y-1.5'}>
                  <Label>{translate('movementShare.fields')}</Label>

                  <div className={'flex flex-wrap gap-x-4 gap-y-2'}>
                    {FIELD_OPTIONS.map(field => (
                      <label className={'flex items-center gap-2 text-sm'} key={field}>
                        <Checkbox
                          checked={fields.includes(field)}
                          onCheckedChange={value => toggleField(field, !!value)}
                        />

                        {translate(`movementShare.field.${field}`)}
                      </label>
                    ))}
                  </div>
                </div>

                {hasActiveFilters ? (
                  <label className={'flex items-center gap-2 text-sm'}>
                    <Checkbox checked={applyFilters} onCheckedChange={value => setApplyFilters(!!value)} />

                    {translate('movementShare.applyFilters')}
                  </label>
                ) : null}
              </div>

              {/* Existing links */}
              <div className={'space-y-2'}>
                <div className={'text-xs font-semibold uppercase tracking-wide text-muted-foreground'}>
                  {translate('movementShare.existing')}
                </div>

                {!shares || shares.filter(share => !share.revokedAt).length === 0 ? (
                  <p className={'text-sm text-muted-foreground'}>{translate('movementShare.none')}</p>
                ) : (
                  shares
                    .filter(share => !share.revokedAt)
                    .map(share => (
                      <div className={'space-y-2 rounded-lg border border-border p-3'} key={share.id}>
                        {/* Header: name + copy link / revoke */}
                        <div className={'flex items-center gap-2'}>
                          <div className={'flex min-w-0 flex-1 items-center gap-1.5 text-sm font-semibold'}>
                            {share.hasPin ? <Lock className={'h-3.5 w-3.5 shrink-0 text-emerald-600'} /> : null}

                            <span className={'truncate text-emerald-600'}>
                              {share.label || translate('movementShare.untitled')}
                            </span>
                          </div>

                          <Button onClick={() => copy(share.id)} size={'small'}>
                            <Copy className={'mr-1 h-4 w-4'} />
                            {translate('movementShare.copyLink')}
                          </Button>

                          <Button
                            danger
                            isLoading={revoke.isLoading}
                            onClick={() => revoke.mutate(share.id)}
                            size={'small'}
                          >
                            <Trash2 className={'h-4 w-4'} />
                          </Button>
                        </div>

                        {/* URL */}
                        <div className={'truncate rounded bg-muted/50 px-2 py-1 text-[11px] text-muted-foreground'}>
                          {shareUrl(share.id)}
                        </div>

                        {/* Config — a tidy label-over-value grid. */}
                        <div className={'grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4'}>
                          <ConfigItem label={translate('movementShare.pin')}>
                            {share.pin ? (
                              <button
                                className={
                                  'inline-flex items-center gap-1 font-mono font-medium text-foreground hover:text-primary'
                                }
                                onClick={() => copyValue(share.pin as string, translate('movementShare.pinCopied'))}
                                type={'button'}
                              >
                                <KeyRound className={'h-3 w-3'} />
                                {share.pin}
                              </button>
                            ) : (
                              translate('movementShare.noPin')
                            )}
                          </ConfigItem>

                          <ConfigItem label={translate('movementShare.range')}>
                            {share.fromDate || share.toDate
                              ? `${fmtDate(share.fromDate)} → ${fmtDate(share.toDate)}`
                              : translate('movementShare.allDates')}
                          </ConfigItem>

                          <ConfigItem label={translate('movementShare.expiresAt')}>
                            {share.expiresAt ? fmtDate(share.expiresAt) : translate('movementShare.never')}
                          </ConfigItem>

                          <ConfigItem className={'col-span-2 sm:col-span-1'} label={translate('movementShare.fields')}>
                            {(share.fields ?? []).map(field => translate(`movementShare.field.${field}`)).join(', ')}
                          </ConfigItem>

                          {share.filters && Object.keys(share.filters).length > 0 ? (
                            <ConfigItem
                              className={'col-span-2 sm:col-span-4'}
                              label={translate('movementShare.filters')}
                            >
                              {Object.keys(share.filters)
                                .map(key => translate(`movementShare.filter.${key}`, { defaultValue: key }))
                                .join(', ')}
                            </ConfigItem>
                          ) : null}
                        </div>
                      </div>
                    ))
                )}
              </div>

              {/* Create — pinned at the end of the modal. */}
              <Button
                className={'w-full'}
                disabled={fields.length === 0 || pinInvalid}
                isLoading={create.isLoading}
                onClick={onCreate}
              >
                <Link2 className={'mr-1.5 h-4 w-4'} />
                {translate('movementShare.create')}
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
